import { QueryClient } from '@tanstack/react-query';
import {
  DASHBOARD_KEY_SEGMENT,
  DASHBOARD_STATS_KEY_SEGMENTS,
} from '@/features/dashboard/tours/api/tourDashboardApi.queryKeys.ts';
import { ATTENTION_KEY_SEGMENT } from '@/features/dashboard/tours/api/tourAttentionApi.queryKeys.ts';
import { TOUR_BASE_QUERY_KEY } from '@/features/dashboard/tours/api/toursApi.queryKeys.ts';

const isDashboardStatsKey = (queryKey: readonly unknown[]): boolean =>
  queryKey.includes(DASHBOARD_KEY_SEGMENT) &&
  DASHBOARD_STATS_KEY_SEGMENTS.some((segment) => queryKey.includes(segment));

/**
 * Podsumowanie, opcje, sprzedaż i sprawy jednego wyjazdu — albo wszystkich,
 * gdy `tourId` nie jest znany.
 */
export const isTourStatsQueryKey = (
  queryKey: readonly unknown[],
  tourId?: string,
): boolean =>
  queryKey[0] === TOUR_BASE_QUERY_KEY &&
  (tourId === undefined || queryKey[1] === tourId) &&
  (isDashboardStatsKey(queryKey) || queryKey.includes(ATTENTION_KEY_SEGMENT));

/**
 * Statystyki wyjazdu po zmianie, która ruszyła rezerwacje, uczestników albo
 * opcje. Finanse mają własnego strażnika (`invalidateFinanceQueries.ts`) i tu
 * nie wchodzą: wpłata rusza wprawdzie progi lejka, ale `staleTime` zapytań to
 * 60 sekund, a `useQuery` i tak odświeża się przy wejściu na zakładkę —
 * zmiatanie sprzedaży przy każdej transakcji kosztowałoby żądanie za żądaniem.
 *
 * Sprawy „Wymaga uwagi” wchodzą do obu strażników: limit opcji i rezygnacja
 * zmieniają je tak samo jak zwrot czy wpłata.
 *
 * Bez `tourId` zmiatamy wszystkie wyjazdy: rezygnacja i paczka zmian znają samo
 * `bookingId`, a przez nie potrafią ruszyć wyjazd inny niż ten na ekranie. To ta
 * sama decyzja, którą podjął `invalidateFinanceQueries.ts`; właściwym lekiem,
 * gdyby okazała się zbyt kosztowna, jest `tourId` w odpowiedzi mutacji.
 *
 * Hooków `tourConfig` tu nie ma świadomie — unieważniają `toursApiQueryKeys.all()`,
 * a React Query dopasowuje klucze prefiksowo, więc klucze dashboardu lecą razem
 * z nimi.
 */
export const invalidateTourStatsQueries = (
  queryClient: QueryClient,
  tourId?: string,
) =>
  queryClient.invalidateQueries({
    predicate: (query) => isTourStatsQueryKey(query.queryKey, tourId),
    refetchType: 'active',
  });
